import { useMemo } from 'react';
import { createHook, createSelector, type BoundActions } from 'react-sweet-state';

import { DEFAULT_ITEM_TYPE, DEFAULT_SUBITEM_TYPE } from '../../../../common/constants';
import { EPIC_FIELDTYPE, type Field } from '../../../../common/constants/fields';
import {
	ITEM,
	LIST,
	NESTED_SUBTASK,
	SUBITEM,
	SUBTASK,
	TASK,
} from '../../../../common/constants/hierarchy';
import type {
	AvailableJiraFields,
	State,
	ThirdPartyFieldConfigState,
	ThirdPartyItemTypeFieldMapping,
} from '../../../../common/types';
import {
	type FormatMessage,
	getFieldNameForHierarchyItem,
} from '../../../../common/utils/third-party-mapping';

import type { SetThirdPartyItemTypeFieldMappingAction } from '../../actions/set-third-party-field-mapping';
import { Store } from '../../main';

type AvailableItem = Field & { isDisabled: boolean };

export type HierarchyMappingType = {
	item: Field;
	mappedItem: Field;
	availableItems: AvailableItem[];
	isDisabled: boolean;
	onClick?: (selectedItem: Field) => void;
	showTooltip?: boolean;
};

type Props = { types: string[]; formatMessage: FormatMessage };
export type ThirdPartyHierarchyMappingActions = {
	setThirdPartyFieldMapping: SetThirdPartyItemTypeFieldMappingAction;
};

export type ThirdPartyHierarchyMappingState = {
	data: HierarchyMappingType[];
	// IMP-268 Improvement: Consolidate these two fields into a single object
	selectedHierarchyItem: string | null;
	selectedHierarchyMappedItem: Field | null;
};

export type UseThirdPartyHierarchy = (
	props: Props,
) => [
	ThirdPartyHierarchyMappingState | null,
	BoundActions<State, ThirdPartyHierarchyMappingActions>,
];

export const transformToHierarchyMapping = (
	type: string,
	itemTypeMapping: ThirdPartyItemTypeFieldMapping | null,
	availableJiraFields: AvailableJiraFields,
	mappingFieldId: string | null,
	formatMessage: FormatMessage,
) => {
	const defaultData: HierarchyMappingType = {
		item: {
			fieldId: type,
			fieldName: getFieldNameForHierarchyItem(type, formatMessage),
			fieldType: type,
		},
		mappedItem: {
			fieldId: type,
			fieldName: getFieldNameForHierarchyItem(type, formatMessage),
			fieldType: type,
		},
		availableItems: [],
		isDisabled: true,
		showTooltip: false,
	};

	if (type === NESTED_SUBTASK || type === SUBITEM) {
		return {
			...defaultData,
			mappedItem: {
				fieldId: SUBTASK,
				fieldName: getFieldNameForHierarchyItem(SUBTASK, formatMessage),
				fieldType: SUBTASK,
			},
			showTooltip: type === NESTED_SUBTASK,
		};
	}

	if (type === ITEM) {
		return {
			...defaultData,
			mappedItem: {
				fieldId: TASK,
				fieldName: getFieldNameForHierarchyItem(TASK, formatMessage),
				fieldType: TASK,
			},
		};
	}

	if (type === LIST) {
		return {
			...defaultData,
			mappedItem: {
				fieldId: EPIC_FIELDTYPE,
				fieldName: getFieldNameForHierarchyItem(EPIC_FIELDTYPE, formatMessage),
				fieldType: EPIC_FIELDTYPE,
			},
			showTooltip: true,
		};
	}

	if (!itemTypeMapping) {
		return defaultData;
	}

	const { thirdPartyFieldMappings, includedJiraFieldIds } = itemTypeMapping;

	if (type === mappingFieldId) {
		const thirdPartyFieldMapping = thirdPartyFieldMappings[mappingFieldId];
		if (!thirdPartyFieldMapping) {
			return defaultData;
		}
		const { mappedJiraField, mappingContext, compatibleExistingFieldTypes, thirdPartyField } =
			thirdPartyFieldMapping;

		const availableItems = compatibleExistingFieldTypes
			.flatMap((fieldType) => availableJiraFields[fieldType] ?? [])
			.sort((jiraField) =>
				!!mappedJiraField.fieldId && jiraField.fieldId === mappedJiraField.fieldId ? -1 : 0,
			)
			.map((jiraField) => {
				const isDisabled = !!jiraField.fieldId && !!includedJiraFieldIds.has(jiraField.fieldId);
				return {
					...jiraField,
					isDisabled,
					fieldName: getFieldNameForHierarchyItem(jiraField.fieldName, formatMessage),
				};
			});

		return {
			item: thirdPartyField,
			mappedItem: {
				...mappedJiraField,
				fieldName: getFieldNameForHierarchyItem(mappedJiraField.fieldName, formatMessage),
			},
			availableItems,
			isDisabled: mappingContext.isFieldDisabled,
			showTooltip: true,
		};
	}

	return defaultData;
};

export const thirdPartyHierarchySelector = createSelector(
	(state: State): ThirdPartyFieldConfigState | null =>
		state.thirdPartyState.thirdPartyFieldConfigState,
	(_, props: Props): Props => props,
	(newState, { types, formatMessage }): ThirdPartyHierarchyMappingState | null => {
		if (newState == null) {
			return null;
		}
		const { fieldMappingState, availableJiraFields, hierarchyMapping } = newState;
		if (!hierarchyMapping) {
			return null;
		}

		let selectedHierarchyItem = null;
		let selectedHierarchyMappedItem = null;
		let thirdPartyItemTypeFieldMapping = null;

		const itemType = hierarchyMapping.itemType || DEFAULT_ITEM_TYPE;

		// For multi-entity, the keys on the fieldMappingState is the entity name
		// If the itemType is not in the fieldMappingState default
		// the thirdPartyItemTypeFieldMapping to null
		if (Object.keys(fieldMappingState).includes(itemType)) {
			const currentItemTypeMapping =
				fieldMappingState[hierarchyMapping.itemType || DEFAULT_ITEM_TYPE];

			const subItemTypeMapping = fieldMappingState[DEFAULT_SUBITEM_TYPE];

			thirdPartyItemTypeFieldMapping = {
				...currentItemTypeMapping,
				// Add the included fields from the subitem type
				includedJiraFieldIds: new Set([
					...currentItemTypeMapping.includedJiraFieldIds,
					...(subItemTypeMapping?.includedJiraFieldIds || []),
				]),
			};
		}
		const data = types.map((type) => {
			const item = transformToHierarchyMapping(
				type,
				thirdPartyItemTypeFieldMapping,
				availableJiraFields,
				hierarchyMapping.mappingFieldId,
				formatMessage,
			);
			if (type === hierarchyMapping.mappingFieldId) {
				selectedHierarchyItem = item.mappedItem.fieldName;
				selectedHierarchyMappedItem = item.mappedItem;
			}
			return item;
		});

		return {
			data,
			selectedHierarchyItem,
			selectedHierarchyMappedItem,
		};
	},
);

export const useThirdPartyHierarchyStateInner = createHook(Store, {
	selector: thirdPartyHierarchySelector,
});

export const useThirdPartyHierarchyState: UseThirdPartyHierarchy = (props: Props) => {
	const [state, { setThirdPartyFieldMapping }] = useThirdPartyHierarchyStateInner(props);

	const actions = useMemo(
		() => ({
			setThirdPartyFieldMapping: setThirdPartyFieldMapping.bind(null, DEFAULT_ITEM_TYPE),
		}),
		[setThirdPartyFieldMapping],
	);

	return [state, actions];
};
